import React from "react";
import { Event } from "./types/events";
import { Resource } from "./types/resources";
import { ViewType } from "./types/common";
import {
  ResourceGroup,
  SchedulerTheme,
  SchedulerViewDefinition,
  TimelineScrollTarget,
  ViewInteractions,
  ViewConfig,
} from "./types/views";
import { getSchedulerViewByType } from "./viewRegistry";

interface SchedulerGridProps {
  events: Event[];
  resources?: Resource[];
  getEventResourceIds?: (event: Event) => (string | number)[];
  currentView: ViewType;
  currentDate: Date;
  startHour?: number;
  endHour?: number;
  onEventChange?: (event: Event) => void;
  onTimeSlotClick?: ViewInteractions["onTimeSlotClick"];
  onEventClick?: ViewInteractions["onEventClick"];
  onResourceClick?: (resource: Resource) => void;
  customViews?: SchedulerViewDefinition[];
  visibleViewTypes?: ViewType[];
  zoom?: number;
  selectedDateRange?: {
    start: Date;
    end: Date;
  };
  viewConfig?: ViewConfig;
  showTooltip?: boolean;
  onEventDrop?: (
    eventId: string,
    newStartTime: Date,
    newResource?: Resource
  ) => void | Promise<any>;
  isDraggable?: boolean;
  useDateRange?: boolean;
  schedulerTheme?: SchedulerTheme;
  resourceGroups?: ResourceGroup[];
  scrollTarget?: TimelineScrollTarget | null;
  highlightedEventId?: string | null;
}

const SchedulerGrid: React.FC<SchedulerGridProps> = ({
  events,
  resources = [],
  getEventResourceIds,
  currentView,
  currentDate,
  startHour = 0,
  endHour = 24,
  onEventChange,
  onEventClick,
  onTimeSlotClick,
  onResourceClick,
  customViews,
  visibleViewTypes,
  zoom = 1,
  selectedDateRange,
  viewConfig,
  showTooltip,
  onEventDrop,
  isDraggable = true,
  useDateRange = false,
  schedulerTheme,
  resourceGroups,
  scrollTarget,
  highlightedEventId,
}) => {
  const handleEventClick: ViewInteractions["onEventClick"] = (
    event,
    element,
    view
  ) => {
    onEventClick?.(event, element, view);
  };

  const handleTimeSlotClick: ViewInteractions["onTimeSlotClick"] = (
    slot,
    element
  ) => {
    onTimeSlotClick?.(slot, element);
  };

  const selectedView = getSchedulerViewByType(
    currentView,
    customViews,
    visibleViewTypes
  );

  if (!selectedView) {
    return <div>View not implemented.</div>;
  }

  const SelectedViewComponent = selectedView.component;

  return (
    <div className="scheduler-grid" style={{ height: "100%", overflow: "hidden" }}>
      <SelectedViewComponent
        events={events}
        resources={resources}
        getEventResourceIds={getEventResourceIds}
        currentDate={currentDate}
        startHour={startHour}
        endHour={endHour}
        onEventChange={onEventChange}
        onEventDrop={onEventDrop}
        onEventClick={handleEventClick}
        onTimeSlotClick={handleTimeSlotClick}
        onResourceClick={onResourceClick}
        zoom={zoom}
        selectedDateRange={selectedDateRange}
        viewConfig={viewConfig}
        showTooltip={showTooltip}
        isDraggable={isDraggable}
        useDateRange={useDateRange}
        schedulerTheme={schedulerTheme}
        resourceGroups={resourceGroups}
        scrollTarget={scrollTarget}
        highlightedEventId={highlightedEventId}
      />
    </div>
  );
};

export default SchedulerGrid;
